import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { submitLeadForm } from '../data/mock';
import { trackLeadFormSubmit } from '../lib/gtag';
import { getApiBase } from '@/lib/apiBase';
import { trackEvent } from '@/lib/trackEvent';
import { toast } from 'sonner';

const LeadCaptureSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    class: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formStartedRef = useRef(false);
  const onLeadFormInteract = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    void trackEvent('form_started', { meta: { surface: 'landing_lead', source: 'get_started_section' } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const web3formsKey = process.env.REACT_APP_WEB3FORMS_KEY || '';
      
      // If no Web3Forms key configured, store in localStorage (development mode)
      if (!web3formsKey || web3formsKey.includes('YOUR_ACCESS_KEY')) {
        const result = await submitLeadForm(formData);
        toast.success(result.message);
        void trackEvent('form_submitted', { meta: { surface: 'landing_lead', channel: 'mock' } });
      } else {
        // Submit to Web3Forms (sends to email)
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_key: web3formsKey,
            subject: 'New Lead from ChooseRightCareer Website',
            from_name: 'ChooseRightCareer Lead Form',
            name: formData.name,
            class: formData.class,
            phone: formData.phone,
            email: formData.email,
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          toast.success(
            "You're on the list, we usually reply within 24 hours on WhatsApp or email."
          );
          trackLeadFormSubmit();
          try {
            const apiBase = getApiBase();
            const lr = await fetch(`${apiBase}/api/landing-leads`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                class: formData.class,
                source: 'landing_web3',
              }),
            });
            if (!lr.ok) {
              void trackEvent('form_submitted', {
                meta: { surface: 'landing_lead', channel: 'landing_leads_http_fail', status: lr.status },
              });
            }
            /* Success: server logs `form_submitted` via logFunnelEvent(lead_created). */
          } catch {
            void trackEvent('form_submitted', { meta: { surface: 'landing_lead', channel: 'landing_leads_fetch_failed' } });
          }
        } else {
          toast.error('Something went wrong. Please try again.');
        }
      }
      
      setFormData({
        name: '',
        class: '',
        phone: '',
        email: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-teal-50 to-blue-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute top-20 right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="grid md:grid-cols-2">
              {/* Left - Content */}
              <div className="bg-gradient-to-br from-blue-600 to-teal-600 p-12 text-white flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 w-fit">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Start Your Journey</span>
                </div>

                <h2 className="font-display text-4xl font-bold mb-6 leading-tight tracking-tight">
                  Tell us you are ready for your Career DNA read
                </h2>

                <p className="text-blue-100 text-lg mb-2">
                  Drop your basics and we walk you through what happens next, calm steps, real humans on WhatsApp or
                  email, zero spam energy.
                </p>
                <p className="text-teal-100/95 text-sm mb-8">
                  We typically respond within <strong className="text-white">24 hours</strong> (often faster on WhatsApp).
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Structured Career DNA discovery, not a random quiz</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Clear PDF report in everyday language</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Meaningful insights in about 8 to 10 minutes</span>
                  </div>
                </div>
              </div>

              {/* Right - Form */}
              <div className="p-12 bg-white">
                <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">Get started now</h3>
                <p className="text-sm text-gray-500 mb-6">Takes under a minute. No spam, ever.</p>
                <form onSubmit={handleSubmit} onFocusCapture={onLeadFormInteract} className="space-y-5">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Full Name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>

                  <div>
                    <select
                      required
                      value={formData.class}
                      onChange={(e) => setFormData({...formData, class: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-700"
                    >
                      <option value="">Select Your Class</option>
                      <option value="8">Class 8</option>
                      <option value="9">Class 9</option>
                      <option value="10">Class 10</option>
                      <option value="11">Class 11</option>
                      <option value="12">Class 12</option>
                    </select>
                  </div>

                  <div>
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 py-6 text-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Know your Career DNA'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>

                <p className="text-sm text-gray-500 mt-4 text-center leading-relaxed">
                  We respect your privacy. Your details are only used to send your report and follow up, see our Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
